// Vite entrypoint. Anything in entrypoints/ is a bundle the layout can include
// by name; everything else under app/javascript/ is reached through imports.

import { createInertiaApp } from '@inertiajs/react'
import { createRoot, hydrateRoot } from 'react-dom/client'

import './application.css'

// Eagerly bundle every page component. `import.meta.glob` is resolved at build
// time, so adding a page is adding a file — there is no registry to maintain
// and no chance of a route that resolves in development and 404s in production.
//
// Eager, not lazy, deliberately: lazy loading would split each page into its own
// chunk and add a network round trip to the first render of every route. Revisit
// when the bundle is big enough to measure, not before.
const pages = import.meta.glob('../pages/**/*.jsx', { eager: true })

createInertiaApp({
  // Rails sends a component name like "Grader/Report"; this maps it to
  // app/javascript/pages/Grader/Report.jsx.
  resolve: (name) => {
    const page = pages[`../pages/${name}.jsx`]
    if (!page) {
      throw new Error(
        `Inertia page "${name}" not found. Expected app/javascript/pages/${name}.jsx — ` +
        `known pages: ${Object.keys(pages).join(', ')}`
      )
    }
    return page
  },

  // Hydrate over build-time HTML where there is some, mount into an empty node
  // otherwise. The nine marketing pages are rendered during `bin/vite build`
  // and served with `data-server-rendered="true"` — see vite-prerender.mjs and
  // app/views/inertia.html.erb.
  //
  // **This branch is the whole client-side cost of prerendering, and its
  // absence was the whole blocker.** Inertia's own default setup already does
  // exactly this; providing a `setup` at all replaces it, so the custom one
  // here called `createRoot` unconditionally and would have thrown away every
  // server-rendered node and redrawn it — correct on screen, and the prerender
  // would have bought nothing but bytes.
  setup({ el, App, props }) {
    const app = <App {...props} />
    if (el.hasAttribute('data-server-rendered')) {
      hydrateRoot(el, app)
    } else {
      createRoot(el).render(app)
    }
  },

  // The bar Inertia shows during a page visit. Amber is the design system's
  // action color; collection latency is handled by real progress surfaces, not
  // by this.
  progress: { color: '#F5A623' },
})
